import type { ComponentProps } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';

import { FormFieldCombobox } from '~/components/FormField';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { useLabelGroupInfiniteList } from '~settings/api/queries/labelGroupInfiniteList';
import type { LabelGroupListQueryVariables } from '~settings/api/queries/labelGroupList';
import { useLabelGroupList } from '~settings/api/queries/labelGroupList';
import { useDropdownPageSize } from '~settings/hooks/useDropdownPageSize';
import { toDropdownList } from '~settings/utils/transformers';

interface SelectLabelGroupsProps
  extends Omit<
      ComponentProps<typeof FormFieldCombobox>,
      | 'clientSearch'
      | 'displaySelectedOptions'
      | 'label'
      | 'options'
      | 'onSearchChange'
      | 'search'
    >,
    Partial<Pick<ComponentProps<typeof FormFieldCombobox>, 'label'>> {
  withSpecialGroups?: boolean;
}

export function SelectLabelGroups({
  multiselect = true,
  name,
  withSpecialGroups = false,
  ...props
}: SelectLabelGroupsProps) {
  const { t } = useTranslation('settings', {
    keyPrefix: 'dropdowns.labelGroup',
  });
  const [labelGroupsEnabled, dropdownPaginationEnabled] = useFeatureFlags([
    FEATURE_FLAGS.ContainerSettingsLabelGroups,
    FEATURE_FLAGS.ContainerSettingsDropdownPagination,
  ]);

  const [currentSearch, setCurrentSearch] = useState('');
  const [search, setSearch] = useState(currentSearch);

  useDebounce(
    () => {
      setSearch(currentSearch);
    },
    250,
    [currentSearch],
  );

  const extraParams: Partial<LabelGroupListQueryVariables> = {
    take: useDropdownPageSize(),
    withSpecialGroups,
  };

  if (dropdownPaginationEnabled && search) {
    extraParams.search = search;
  }

  const { isLoading, labelGroupList } = useLabelGroupList(
    {
      skip: 0,
      ...extraParams,
    },
    {
      enabled: !dropdownPaginationEnabled,
    },
  );

  const {
    isLoading: isLoadingInfinite,
    labelGroupInfiniteList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLabelGroupInfiniteList(extraParams, {
    enabled: dropdownPaginationEnabled,
  });

  const labelGroups =
    labelGroupInfiniteList?.pages.flatMap((page) => page.data) ??
    labelGroupList?.data ??
    [];

  const onLoadMore = async () => {
    if (hasNextPage && dropdownPaginationEnabled && !isFetchingNextPage) {
      await fetchNextPage();
    }
  };

  const count = multiselect ? 2 : 1;

  if (!labelGroupsEnabled) {
    return null;
  }

  return (
    <FormFieldCombobox
      label={t('title', { count })}
      placeholder={t('placeholder', { count })}
      searchPlaceholder={t('searchPlaceholder', { count })}
      {...props}
      clientSearch={!dropdownPaginationEnabled}
      displaySelectedOptions
      multiselect={multiselect}
      name={name}
      onSearchChange={setCurrentSearch}
      options={toDropdownList(labelGroups)}
      readOnly={isLoading || isLoadingInfinite || props.readOnly}
      search={currentSearch}
      onLoadMore={onLoadMore}
      extendOptions
      limitDisplayRows
    />
  );
}
SelectLabelGroups.displayName = 'SelectLabelGroups';
